/**
 * Supabase Authentication Context
 * Prozpector CRM
 */

import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { useToast } from '@/hooks/use-toast';
import { trackSignup, trackTrialStarted, trackLogin, trackLogout, trackPasswordReset } from './tracking-helpers';

interface MFAStatus {
  enabled: boolean;
  factorId: string | null;
  requiresVerification: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signUp: (email: string, password: string, firstName?: string) => Promise<{ user: User | null; error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ user: User | null; error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  // MFA utilities
  checkMFAStatus: () => Promise<MFAStatus>;
  getMFAFactors: () => Promise<{ totp: any[]; phone: any[] }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start with true while checking session
  const { toast } = useToast();

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);

      // Process email queue when user logs in (sends welcome email after verification)
      if (session?.user && _event === 'SIGNED_IN') {
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-email-queue`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          }
        }).catch(err => console.log('Email queue processing triggered:', err));
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, firstName?: string) => {
    try {
      // Calculate trial end date (14 days from now)
      const trialEndsAt = new Date();
      trialEndsAt.setDate(trialEndsAt.getDate() + 14);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName || '',
            plan: 'free',
            trial_ends_at: trialEndsAt.toISOString(),
            onboarding_completed: false,
          },
        },
      });

      if (error) {
        toast({
          title: 'Sign Up Failed',
          description: error.message,
          variant: 'destructive',
        });
        return { user: null, error };
      }

      if (data.user) {
        // Track signup and trial start
        trackSignup(email, 'free');
        trackTrialStarted(14);

        toast({
          title: 'Account Created',
          description: 'Please check your email to verify your account.',
        });

        // Note: Welcome email will be sent automatically after email verification
        // via Supabase database trigger (see database/migrations/114_email_verification_trigger.sql)
      }

      return { user: data.user, error: null };
    } catch (error) {
      const authError = error as AuthError;
      toast({
        title: 'Sign Up Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
      return { user: null, error: authError };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      console.log('🔐 Attempting sign in with Supabase...');
      console.log('Supabase URL:', import.meta.env.VITE_SUPABASE_URL);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('❌ Sign in error:', error);
        toast({
          title: 'Sign In Failed',
          description: error.message,
          variant: 'destructive',
        });
        return { user: null, error };
      }

      console.log('✅ Sign in successful:', data);

      // Track login
      trackLogin();

      toast({
        title: 'Welcome Back!',
        description: 'You have successfully signed in.',
      });

      return { user: data.user, error: null };
    } catch (error) {
      const authError = error as AuthError;
      console.error('❌ Unexpected sign in error:', authError);
      toast({
        title: 'Sign In Error',
        description: authError.message || 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
      return { user: null, error: authError };
    }
  };

  const signOut = async () => {
    try {
      // Track logout before signing out
      trackLogout();

      const { error } = await supabase.auth.signOut();

      if (error) {
        toast({
          title: 'Sign Out Failed',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Signed Out',
        description: 'You have been signed out successfully.',
      });
    } catch (error) {
      toast({
        title: 'Sign Out Error',
        description: 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        toast({
          title: 'Password Reset Failed',
          description: error.message,
          variant: 'destructive',
        });
        return { error };
      }

      // Track password reset request
      trackPasswordReset(email);

      toast({
        title: 'Password Reset Email Sent',
        description: 'Please check your email for password reset instructions.',
      });

      return { error: null };
    } catch (error) {
      const authError = error as AuthError;
      toast({
        title: 'Password Reset Error',
        description: 'An unexpected error occurred.',
        variant: 'destructive',
      });
      return { error: authError };
    }
  };

  // Check MFA status for current user
  const checkMFAStatus = async (): Promise<MFAStatus> => {
    try {
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      const { data: factors } = await supabase.auth.mfa.listFactors();

      const verifiedFactor = factors?.totp.find(f => f.status === 'verified');

      return {
        enabled: !!verifiedFactor,
        factorId: verifiedFactor?.id || null,
        requiresVerification: aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2'
      };
    } catch (error) {
      console.error('Error checking MFA status:', error);
      return {
        enabled: false,
        factorId: null,
        requiresVerification: false
      };
    }
  };

  // Get all MFA factors
  const getMFAFactors = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      return { totp: data.totp || [], phone: data.phone || [] };
    } catch (error) {
      console.error('Error getting MFA factors:', error);
      return { totp: [], phone: [] };
    }
  };

  const value = {
    user,
    session,
    isLoading,
    isAuthenticated: !!user,
    signUp,
    signIn,
    signOut,
    resetPassword,
    checkMFAStatus,
    getMFAFactors,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
